<template>
    <div class="relative bg-white shadow-md rounded-xl p-4 m-4">
        <MonkeyLoader v-if="isLoading" />

        <h2 class="text-xl font-semibold mb-4 text-gray-800">
            Invoice SPK Report
        </h2>

        <div class="space-y-4">
            <div class="flex items-end gap-4">
                <div class="w-2/12">
                    <label class="block text-gray-700 text-sm font-bold mb-2">
                        Subcon:
                    </label>
                </div>
                <div class="w-8/12">
                    <select class="input-border shadow w-full py-2 px-3" v-model="form.subconId"
                        :disabled="form.isAllSubcons" :class="{ 'bg-gray-200': form.isAllSubcons }">
                        <option value="">-- Pilih Subcon --</option>
                        <option v-for="subcon in subconOptions" :key="subcon.id" :value="subcon.id">
                            {{ subcon.name }}
                        </option>
                    </select>
                </div>
                <div class="w-2/12 flex items-center pb-2">
                    <input type="checkbox" id="allSubcons" v-model="form.isAllSubcons" class="h-4 w-4 mr-2">
                    <label for="allSubcons">ALL</label>
                </div>
            </div>

            <div class="flex items-end gap-4">
                <div class="w-2/12">
                    <label class="block text-gray-700 text-sm font-bold mb-2">
                        SPK #:
                    </label>
                </div>
                <div class="w-8/12">
                    <select class="input-border shadow w-full py-2 px-3" v-model="form.spkId" :disabled="form.isAllSpks"
                        :class="{ 'bg-gray-200': form.isAllSpks }">
                        <option value="">-- Pilih SPK # --</option>
                        <option v-for="spk in spkOptions" :key="spk.id" :value="spk.id">
                            {{ spk.spkNum }}
                        </option>
                    </select>
                </div>
                <div class="w-2/12 flex items-center pb-2">
                    <input type="checkbox" id="allSpks" v-model="form.isAllSpks" class="h-4 w-4 mr-2">
                    <label for="allSpks">ALL</label>
                </div>
            </div>

            <div class="flex items-end gap-4">
                <div class="w-2/12">
                    <label class="block text-gray-700 text-sm font-bold mb-2">
                        Job Name:
                    </label>
                </div>
                <div class="w-8/12">
                    <select class="input-border shadow w-full py-2 px-3" v-model="form.jobId" :disabled="form.isAllJobs"
                        :class="{ 'bg-gray-200': form.isAllJobs }">
                        <option value="">-- Pilih Job --</option>
                        <option v-for="job in jobOptions" :key="job.id" :value="job.id">
                            {{ job.name }}
                        </option>
                    </select>
                </div>
                <div class="w-2/12 flex items-center pb-2">
                    <input type="checkbox" id="allJobs" v-model="form.isAllJobs" class="h-4 w-4 mr-2">
                    <label for="allJobs">ALL</label>
                </div>
            </div>

            <div class="flex items-end gap-4">
                <div class="w-2/12">
                    <label class="block text-gray-700 text-sm font-bold mb-2">
                        Invoice SPK #:
                    </label>
                </div>
                <div class="w-8/12">
                    <select class="input-border shadow w-full py-2 px-3" v-model="form.invoiceId"
                        :disabled="form.isAllInvoices" :class="{ 'bg-gray-200': form.isAllInvoices }" required>
                        <option value="">-- Pilih Invoice SPK # --</option>
                        <option v-for="invoice in invoiceOptions" :key="invoice.id" :value="invoice.id">
                            {{ invoice.invoiceNum }}
                        </option>
                    </select>
                </div>
                <div class="w-2/12 flex items-center pb-2">
                    <input type="checkbox" id="allInvoices" v-model="form.isAllInvoices" class="h-4 w-4 mr-2">
                    <label for="allInvoices">ALL</label>
                </div>
            </div>

            <div class="flex items-end gap-4">
                <div class="w-2/12">
                    <label class="block text-gray-700 text-sm font-bold mb-2">
                        Periode Date:
                    </label>
                </div>
                <div class="w-4/12">
                    <input class="input-border shadow w-full py-2 px-3" type="date" v-model="form.startDate"
                        :disabled="form.isAllDates" :class="{ 'bg-gray-200': form.isAllDates }" />
                </div>
                <div class="w-4/12">
                    <input class="input-border shadow w-full py-2 px-3" type="date" v-model="form.endDate"
                        :disabled="form.isAllDates" :class="{ 'bg-gray-200': form.isAllDates }" />
                </div>
                <div class="w-2/12 flex items-center pb-2">
                    <input type="checkbox" id="allDates" v-model="form.isAllDates" class="h-4 w-4 mr-2">
                    <label for="allDates">ALL</label>
                </div>
            </div>

            <div class="flex justify-end gap-2">
                <button @click="handleLoadData" :disabled="isLoading"
                    :class="{ 'opacity-50 cursor-not-allowed': isLoading }"
                    class="bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-8 rounded focus:outline-none focus:shadow-outline">
                    <span v-if="isLoading">Loading...</span>
                    <span v-else>Load Data</span>
                </button>
                <button @click="handleExportExcel" :disabled="!preparedData || preparedData.length === 0"
                    :class="{ 'opacity-50 cursor-not-allowed': !preparedData || preparedData.length === 0 }"
                    class="bg-green-600 hover:bg-green-800 text-white font-bold py-2 px-8 rounded focus:outline-none focus:shadow-outline">
                    Export to Excel
                </button>
            </div>
        </div>

        <div v-if="preparedData && preparedData.length > 0" class="mt-8 border-t-2 pt-6">
            <h3 class="text-xl font-semibold mb-4 text-gray-800">
                Invoice Report Table
            </h3>
            <div class="overflow-x-auto">
                <table class="min-w-full table-auto border-collapse">
                    <thead class="bg-gray-200">
                        <tr>
                            <th class="px-4 py-2 border-b text-left">No</th>
                            <th class="px-4 py-2 border-b text-left">Site Name</th>
                            <th class="px-4 py-2 border-b text-left">Area</th>
                            <th class="px-4 py-2 border-b text-left">PO #</th>
                            <th class="px-4 py-2 border-b text-left">Project Name</th>
                            <th class="px-4 py-2 border-b text-left">Subcon Name</th>
                            <th class="px-4 py-2 border-b text-left">SPK #</th>
                            <th class="px-4 py-2 border-b text-left">Job Name</th>
                            <th class="px-4 py-2 border-b text-left">SPK Date</th>
                            <th class="px-4 py-2 border-b text-right">SPK Amount (Rp)</th>
                            <th class="px-4 py-2 border-b text-left">Invoice # (GSPE)</th>
                            <th class="px-4 py-2 border-b text-left">Inv Date (GSPE)</th>
                            <th class="px-4 py-2 border-b text-left">Invoice # (Subcon)</th>
                            <th class="px-4 py-2 border-b text-right">Invoice Amount (Rp)</th>
                            <th class="px-4 py-2 border-b text-left">Payment Date</th>
                            <th class="px-4 py-2 border-b text-left">Remark</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in paginatedData" :key="index"
                            :class="{ 'bg-gray-50': index % 2 === 1, 'bg-white': index % 2 === 0 }">
                            <td class="px-4 py-2 border-b">{{ (currentPage - 1) * rowsPerPage + index + 1 }}</td>
                            <td class="px-4 py-2 border-b">{{ item.sitename || '-' }}</td>
                            <td class="px-4 py-2 border-b">{{ item.area || '-' }}</td>
                            <td class="px-4 py-2 border-b">{{ item.pocustnum || '-' }}</td>
                            <td class="px-4 py-2 border-b">{{ item.projectName || '-' }}</td>
                            <td class="px-4 py-2 border-b">{{ item.entityName || '-' }}</td>
                            <td class="px-4 py-2 border-b">{{ item.spkNum || '-' }}</td>
                            <td class="px-4 py-2 border-b">{{ item.jobName || '-' }}</td>
                            <td class="px-4 py-2 border-b">{{ formatDate(item.spkDate) }}</td>
                            <td class="px-4 py-2 border-b text-right">{{ formatCurrency(item.spkAmount) }}</td>
                            <td class="px-4 py-2 border-b">{{ item.invoiceGspeNum || '-' }}</td>
                            <td class="px-4 py-2 border-b">{{ formatDate(item.invoiceGspeDate) }}</td>
                            <td class="px-4 py-2 border-b">{{ item.invoiceSubconNum || '-' }}</td>
                            <td class="px-4 py-2 border-b text-right">{{ formatCurrency(item.invoiceAmount) }}</td>
                            <td class="px-4 py-2 border-b">{{ formatDate(item.paymentDate) }}</td>
                            <td class="px-4 py-2 border-b">{{ item.remark || '-' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="flex justify-between items-center mt-4">
                <button @click="prevPage" :disabled="currentPage === 1"
                    class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-l">
                    Previous
                </button>
                <span class="text-sm text-gray-700">
                    Page {{ currentPage }} of {{ totalPages }}
                </span>
                <button @click="nextPage" :disabled="currentPage === totalPages"
                    class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-r">
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

export default {
    components: {
        MonkeyLoader
    },
    data() {
        return {
            form: {
                subconId: '',
                isAllSubcons: true,
                spkId: '',
                isAllSpks: true,
                jobId: '',
                isAllJobs: true,
                invoiceId: '',
                isAllInvoices: true,
                startDate: '',
                endDate: '',
                isAllDates: false,
            },
            subconOptions: [],
            spkOptions: [],
            jobOptions: [],
            invoiceOptions: [],
            preparedData: null,
            isLoading: false,
            currentPage: 1,
            rowsPerPage: 10,
        };
    },
    computed: {
        paginatedData() {
            if (!this.preparedData) {
                return [];
            }
            const start = (this.currentPage - 1) * this.rowsPerPage;
            const end = start + this.rowsPerPage;
            return this.preparedData.slice(start, end);
        },
        totalPages() {
            if (!this.preparedData) {
                return 1;
            }
            return Math.ceil(this.preparedData.length / this.rowsPerPage);
        },
    },
    methods: {
        setDefaultDates() {
            const today = new Date();
            const endDate = today.toISOString().split('T')[0];
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            const startDate = thirtyDaysAgo.toISOString().split('T')[0];

            this.form.startDate = startDate;
            this.form.endDate = endDate;
        },
        async handleLoadData() {
            this.isLoading = true;
            this.currentPage = 1;

            Swal.fire({
                title: 'Mempersiapkan Laporan',
                text: 'Mengambil data, mohon tunggu...',
                allowOutsideClick: false,
                didOpen: () => { Swal.showLoading(); },
            });

            try {
                const params = {
                    subconId: this.form.isAllSubcons ? null : this.form.subconId,
                    spkId: this.form.isAllSpks ? null : this.form.spkId,
                    jobId: this.form.isAllJobs ? null : this.form.jobId,
                    invoiceId: this.form.isAllInvoices ? null : this.form.invoiceId,
                    startDate: this.form.isAllDates ? null : this.form.startDate,
                    endDate: this.form.isAllDates ? null : this.form.endDate,
                };

                const response = await axios.get(`${window.location.origin}/api/reports/invoices`, {
                    params,
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });

                this.preparedData = response.data;

                if (!this.preparedData || this.preparedData.length === 0) {
                    Swal.fire('Tidak Ada Data', 'Tidak ada data yang ditemukan untuk kriteria yang dipilih.', 'info');
                    return;
                }

                Swal.close();
            } catch (error) {
                console.error('Error fetching report data:', error);
                Swal.fire('Error', 'Gagal memuat data laporan.', 'error');
            } finally {
                this.isLoading = false;
            }
        },
        handleExportExcel() {
            if (!this.preparedData || this.preparedData.length === 0) {
                Swal.fire('Data Kosong', 'Silakan klik tombol "Load Data" terlebih dahulu untuk memuat data laporan.', 'warning');
                return;
            }

            const headers = {
                sitename: 'Site Name',
                area: 'Area',
                pocustnum: 'PO #',
                projectName: 'Project Name',
                entityName: 'Subcon Name',
                spkNum: 'SPK #',
                jobName: 'Job Name',
                spkDate: 'SPK Date',
                spkAmount: 'SPK Amount (Rp)',
                invoiceGspeNum: 'Invoice # (GSPE)',
                invoiceGspeDate: 'Inv Date (GSPE)',
                invoiceSubconNum: 'Invoice # (Subcon)',
                invoiceAmount: 'Invoice Amount (Rp)',
                paymentDate: 'Payment Date',
                remark: 'Remark'
            };

            const excelData = this.preparedData.map(item => {
                let row = {};
                for (const key in headers) {
                    if (key.toLowerCase().includes('date')) {
                        row[headers[key]] = this.formatDate(item[key]);
                    } else if (key.toLowerCase().includes('amount')) {
                        row[headers[key]] = item[key] || 0;
                    } else {
                        row[headers[key]] = item[key] || '';
                    }
                }
                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Report');
            const today = new Date().toISOString().split('T')[0];
            const fileName = `Invoice_SPK_Report_${today}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        },
        formatCurrency(value) {
            if (value === null || value === undefined) return 'Rp 0';
            return `Rp ${value.toLocaleString('id-ID')}`;
        },
        formatDate(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return '-';
            const userTimezoneOffset = date.getTimezoneOffset() * 60000;
            const localDate = new Date(date.getTime() - userTimezoneOffset);
            return localDate.toLocaleDateString('id-ID', {
                day: '2-digit', month: 'short', year: 'numeric'
            });
        },
        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
            }
        },
        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
            }
        },
        async loadSubconOptions() {
            try {
                const response = await axios.get(`${window.location.origin}/api/spksubcon/combined-data`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                const subconMap = new Map();
                response.data.forEach(item => {
                    if (!subconMap.has(item.entityId)) {
                        subconMap.set(item.entityId, {
                            id: item.entityId,
                            name: item.entityName,
                        });
                    }
                });
                this.subconOptions = Array.from(subconMap.values());
            } catch (error) {
                console.error('Error fetching Subcon options:', error);
                Swal.fire('Error', 'Gagal memuat daftar subcon dari API.', 'error');
            }
        },
        async loadJobOptions() {
            try {
                const response = await axios.get(`${window.location.origin}/api/jobfiles`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                this.jobOptions = response.data.map(job => ({
                    id: job.id,
                    name: job.jobname
                }));
            } catch (error) {
                console.error('Error fetching Job options:', error);
                Swal.fire('Error', 'Gagal memuat daftar Job dari API.', 'error');
            }
        },
        async loadSpkOptions() {
            try {
                const response = await axios.get(`${window.location.origin}/api/spksubconfiles/print`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                this.spkOptions = response.data.map(spk => ({
                    id: spk.Id,
                    spkNum: spk.SpkNum,
                }));
            } catch (error) {
                console.error('Error fetching SPK options:', error);
                Swal.fire('Error', 'Gagal memuat daftar SPK dari API.', 'error');
            }
        },
        async loadInvoiceOptions() {
            try {
                const response = await axios.get(`${window.location.origin}/api/invoicesubconall`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                this.invoiceOptions = response.data.map(inv => ({
                    id: inv.id,
                    invoiceNum: inv.invnum,
                }));
            } catch (error) {
                console.error('Error fetching Invoice options:', error);
                Swal.fire('Error', 'Gagal memuat daftar invoice dari API.', 'error');
            }
        },
    },
    mounted() {
        this.setDefaultDates();
        this.loadSubconOptions();
        this.loadSpkOptions();
        this.loadJobOptions();
        this.loadInvoiceOptions();
    },
    watch: {
        'form.isAllSubcons'(newVal) {
            if (newVal) {
                this.form.subconId = '';
            }
        },
        'form.isAllSpks'(newVal) {
            if (newVal) {
                this.form.spkId = '';
            }
        },
        'form.isAllJobs'(newVal) {
            if (newVal) {
                this.form.jobId = '';
            }
        },
        'form.isAllInvoices'(newVal) {
            if (newVal) {
                this.form.invoiceId = '';
            }
        },
        'form.isAllDates'(newVal) {
            if (newVal) {
                this.form.startDate = '';
                this.form.endDate = '';
            } else {
                this.setDefaultDates();
            }
        },
    }
};
</script>

<style scoped>
.input-border {
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.input-border:focus {
    border-color: #007bff;
    box-shadow: none;
}
</style>